import {
  IAppointmentsProgram,
  IServerStatus,
  TQuestionOption,
  TQuestionOptionsByGroup,
} from "../services/types/utils.types";
import { TQuestionnaireEntry } from "../services/types/user.types";
import { IOption } from "../types/types";
import { Dispatch, SetStateAction } from "react";
import { noDataLabel, TExercisesCategory } from "./constants";
import { tWithKeyAttr as t } from "../i18nHelper";
import i18n from "../i18n";
import { featureOptionLabel } from "./featureCreditPackages";
import { FeatureCreditPackageDto } from "../services/api/featureCreditPackagesApi";
export function mergeArrays(
  array1: TQuestionOption[],
  array2: TQuestionOption[]
): TQuestionOption[] {
  return array1.map((obj1) => {
    const obj2 = array2.find((o) => o.id === obj1.id);
    return obj2 || obj1;
  });
}

export function findChanges(
  array1: any[],
  array2: any[]
): {
  descChanged: TQuestionOption[];
  groupIdChanged: TQuestionOption[];
  nextQuestionIdChanged: TQuestionOption[];
  nextquesChanged: TQuestionOption[];
} {
  const descChanged: TQuestionOption[] = [];
  const groupIdChanged: TQuestionOption[] = [];
  const nextQuestionIdChanged: TQuestionOption[] = [];
  const nextquesChanged: TQuestionOption[] = [];

  array1.forEach((obj1) => {
    const obj2 = array2.find((o) => o.Id === obj1.Id);
    if (!obj2) {
      return;
    }

    if (obj1.Desc !== obj2.Desc) {
      descChanged.push(obj2);
    }
    if (obj1.GroupId !== obj2.GroupId) {
      groupIdChanged.push(obj2);
    }
    if (obj1.NextQuestionId !== obj2.NextQuestionId) {
      nextQuestionIdChanged.push(obj2);
    }
    if (obj1.Nextques !== obj2.Nextques) {
      nextquesChanged.push(obj2);
    }
  });

  return {
    descChanged: descChanged,
    groupIdChanged: groupIdChanged,
    nextQuestionIdChanged: nextQuestionIdChanged,
    nextquesChanged: nextquesChanged,
  };
}

export const getCategory = (
  categories: TExercisesCategory[],
  id: number,
): string => {
  const category = categories.find((c) => c.id === id);
  return category ? category.name : t(noDataLabel);
};

export const getTimeFromTimestamp = (date: Date | string) => {
  if (typeof date === "string") {
    date = new Date(date);
  }
  const timestamp = date.toTimeString().split(":");
  return `${timestamp[0]}:${timestamp[1]}`;
};

export function getPrintDate(date: Date, short?: boolean): string {
  return `${date.getDate()}/${date.getMonth() + 1}/${
    !short ? date.getFullYear() : date.getFullYear().toString().substr(-2)
  }`;
}
export const formatDateOptions = (
  questionnaires: TQuestionnaireEntry[],
  questionnaireId: number
): IOption[] => {
  return questionnaires
    .filter((qu) => qu.QuestionnaireId === questionnaireId)
    .map((q) => ({
      label: `${getPrintDate(
        new Date(q.Timestamp),
        true
      )} (${getTimeFromTimestamp(new Date(q.Timestamp))}) (${q.Cycle})`,
      value: q.Timestamp,
    }));
};

export const sortOptionsByGroup = (
  arr: TQuestionOption[]
): TQuestionOptionsByGroup[] => {
  return arr.reduce((acc: TQuestionOptionsByGroup[], obj: TQuestionOption) => {
    // Check if the group already exists in the result array
    let group = acc.find((item) => item.groupId === obj.groupId);

    // If the group doesn't exist, create a new one
    if (!group) {
      group = { groupId: obj.groupId, options: [] };
      acc.push(group);
    }

    // Add the object to the options array of the group
    group.options.push(obj);

    return acc;
  }, []);
};

export const moveOption = (
  draggedOption: TQuestionOption,
  targetOption: TQuestionOption,
  setForm: Dispatch<SetStateAction<TQuestionOption[]>>
) => {
  // setTouched(true);
  setForm((prevOptions: TQuestionOption[]) => {
    const filteredOptions = prevOptions.filter(
      (opt: TQuestionOption) => opt.id !== draggedOption.id
    );

    const targetIndex = filteredOptions.findIndex(
      (opt: TQuestionOption) => opt.id === targetOption.id
    );

    if (targetIndex === -1) return prevOptions; // Return early if target not found

    const targetGroupId = targetOption.groupId;
    const targetGroupOptions = filteredOptions.filter(
      (option) => option.groupId === targetGroupId
    );
    const otherOptions = filteredOptions.filter(
      (option) => option.groupId !== targetGroupId
    );

    const insertIndex =
      draggedOption.seq === null || draggedOption.seq > targetOption.seq
        ? targetIndex // Insert before target
        : targetIndex + 1; // Insert after target

    // Create a new array with the dragged option inserted
    const updatedOptions = [
      ...targetGroupOptions.slice(0, insertIndex),
      { ...draggedOption, groupId: targetGroupId },
      ...targetGroupOptions.slice(insertIndex),
    ];
    return [
      ...updatedOptions.map((opt, index) => ({ ...opt, seq: index + 1 })),
      ...otherOptions,
    ];
  });
};

export const isDateAfterToday = (dateStr: Date): boolean => {
  const inputDate = new Date(dateStr).setHours(0, 0, 0, 0); // Remove time component
  const today = new Date().setHours(0, 0, 0, 0); // Today's date without time

  return inputDate > today; // True if the date is after today
};

export const formatDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
};

export function filterUniquePrograms(
  programs: Partial<IAppointmentsProgram>[]
): Partial<IAppointmentsProgram>[] {
  const uniqueNames = new Set<string | null>();

  return programs.filter((program) => {
    if (program.name && !uniqueNames.has(program.name)) {
      uniqueNames.add(program.name);
      return true;
    }
    return false;
  });
}
export const getScreenName = (id: number): string => {
  return id === 3 ? t("SCREEN_NAME_ADMIN") : id === 2 ? t("SCREEN_NAME_THERAPIST") : t("SCREEN_NAME_CLIENT");
};

export const getStatusClass = (id: number): string => {
  return id === 4
    ? "stuck"
    : id === 3
    ? "done"
    : id === 2
    ? "inProgress"
    : "notYetAddressed";
};

export const formatDateTimeInIsrael = (dateInput: Date | string): string => {
  // Convert input to a Date object if it's a string
  const date = new Date(dateInput);

  // Use Intl.DateTimeFormat to get the date in Israel time
  const israelTime = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jerusalem", // The IANA time zone identifier for Israel
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false, // Use 24-hour format
  }).format(date);

  // Extract the components from the formatted Israel time
  const [formattedDate, formattedTime] = israelTime.split(", ");
  const [day, month, year] = formattedDate.split("/");
  const [hours, minutes] = formattedTime.split(":");

  // Format to desired output (YYYY-MM-DDTHH:mm:ssZ)
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Quota resets arrive as UTC instants that sit on an Israel-time month boundary
// (2026-08-31T21:00:00Z is 1 September in Israel), so the day only reads correctly when the
// rendering is pinned to Asia/Jerusalem instead of the viewer's own zone.
export const formatDateInIsrael = (dateInput: Date | string): string =>
  new Date(dateInput).toLocaleDateString(i18n.language === "he" ? "he-IL" : "en-US", {
    timeZone: "Asia/Jerusalem",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export function isToday(dateString: Date): boolean {
  const today = new Date();
  const date = dateString;

  return (
    today.getDate() === date.getDate() &&
    today.getMonth() === date.getMonth() &&
    today.getFullYear() === date.getFullYear()
  );
}

export const getFileExtension = (url: string): string => {
  return url.split(".").pop()?.toLowerCase() || "";
};
export function isColorDark(hexColor: string): boolean {
  // Remove '#' if present
  const hex = hexColor.replace("#", "");

  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate luminance (perceived brightness)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Threshold: lower = darker
  return brightness < 128;
}

export function parseSummaryText(summary?: string): string {
  if (!summary) {
    return "";
  }

  let text = summary;

  // Extract inner content from markdown code fences if present
  const fencedMatch = text.match(/```(?:json|js|txt)?\s*([\s\S]*?)\s*```/i);
  if (fencedMatch) {
    const inner = fencedMatch[1].trim();
    try {
      const parsed = JSON.parse(inner);
      if (parsed && typeof (parsed as any).summary === "string") {
        return (parsed as any).summary.trim();
      }
      if (typeof parsed === "string") {
        return parsed.trim();
      }
    } catch {}
    text = inner;
  }

  // Try to parse as JSON directly
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof (parsed as any).summary === "string") {
      return (parsed as any).summary.trim();
    }
    if (typeof parsed === "string") {
      return parsed.trim();
    }
  } catch {}

  // Strip any remaining code fences and HTML tags
  text = text.replace(/```/g, "");
  text = text.replace(/<[^>]*>/g, "");

  return text.trim();
}

export function getUpdateReasonLabel(updateReason: string | null, questionnaireId?: number): string {
  // Handle null updateReason - if questionnaireId exists, treat as initial questionnaire
  if (!updateReason) {
    if (questionnaireId) {
      return `${t("HISTORY_REASON_INITIAL")} ${questionnaireId}`;
    }
    return '';
  }

  switch (updateReason) {
    case "1":
    case "InitialQuestionnaire":
      return questionnaireId 
        ? `${t("HISTORY_REASON_INITIAL")} ${questionnaireId}`
        : t("HISTORY_REASON_INITIAL");
    case "2":
    case "NewQuestionnaire":
      return questionnaireId 
        ? `${t("HISTORY_REASON_NEW_QUESTIONNAIRE")} ${questionnaireId}`
        : t("HISTORY_REASON_NEW_QUESTIONNAIRE");
    case "3":
    case "TherapistResponse":
      return t("HISTORY_REASON_THERAPIST_UPDATED");
    default:
      return updateReason;
  }
}

export function getUpdateReasonColor(updateReason: string | null, styles: Record<string, string>): string {
  // Handle null updateReason - default to initial badge color
  if (!updateReason) {
    return styles.historyBadge_initial;
  }

  switch (updateReason) {
    case "1":
    case "InitialQuestionnaire":
      return styles.historyBadge_initial;
    case "2":
    case "NewQuestionnaire":
      return styles.historyBadge_questionnaire;
    case "3":
    case "TherapistResponse":
      return styles.historyBadge_therapist;
    default:
      return styles.historyBadge_initial;
  }
}
export function capitalizeKeys<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(capitalizeKeys) as unknown as T;
  }

  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      (acc as any)[capitalizedKey] = capitalizeKeys((obj as any)[key]);
      return acc;
    }, {} as T);
  }

  return obj;
}
export interface ApiError {
  data: {
    message: string;
  };
}
 export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof (error as ApiError).data === "object" &&
    "message" in (error as ApiError).data
  );
}
export   const timeToMinutes = (time: string) => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const getChangedValues = (
  values: any,
  initialValues: any
): Partial<any> => {
  return Object.entries(values).reduce((acc, [key, value]) => {
    const hasChanged = initialValues[key] !== value;

    if (hasChanged) {
      (acc as any)[key] = value;
    }

    return acc;
  }, {} as Partial<any>);
};

export const getServerStatusText = (
  id: number,
  statuses: IServerStatus[]
): string => {
  
  if (id === 3) return t("SERVER_STATUS_DONE");
  if (id === 2) return t("SERVER_STATUS_IN_PROGRESS");
  return t("SERVER_STATUS_WAITING");
};

export const packageFeatureLabel = (pkg: FeatureCreditPackageDto): string =>
  featureOptionLabel({ featureKey: pkg.featureKey, creditType: pkg.creditType });

export const getMimeType = (fileUrl: string): string => {
  const extension = fileUrl.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "pdf":
      return "application/pdf";
    default:
      return "application/octet-stream";
  }
};
